import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Chip,
  Typography,
  IconButton,
  Checkbox,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Alert,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { fontSizes } from "../design-system/tokens";

/**
 * LWI-557 / LWI-558 — Shared dialog for fanning a single PO action
 * (US-content answer, or admin authorize-shipping) across other matching
 * open PO lines for the same part + program.
 *
 * The parent decides what triggered this dialog and what gets copied; this
 * component just renders the candidates with checkboxes and reports back
 * the selected pids on confirm. Empty candidates → caller should not open
 * the dialog at all (no UX to show "nothing to apply to").
 */
export default function RelatedPosBulkActionDialog({
  open,
  onClose,
  candidates,
  onConfirm,
  title,
  description,
  confirmLabel = "Apply",
  // LWI-821 — rename Cancel → Close. The button only dismisses the dialog
  // without applying anything, so "Close" matches behaviour better and
  // avoids the supplier confusion about whether Cancel rolls back the
  // upstream save.
  cancelLabel = "Close",
}) {
  const [selectedPids, setSelectedPids] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  // LWI-822 — default to nothing selected. The previous "default all"
  // caused unintended bulk operations when a supplier reflexively clicked
  // Apply expecting just the source PO to be affected. The user must now
  // explicitly check the rows they want included.
  useEffect(() => {
    if (open) {
      setSelectedPids(new Set());
      setErrorMessage(null);
    }
  }, [open, candidates]);

  // LWI-870 — candidates flagged isLockedByCci can be shown in the dialog
  // but never selected. Filter them out of "select all" and the per-row
  // toggle. The backend additionally skips them in the actual apply, as a
  // defense in depth, with reason='cci_locked' in the response.
  const isRowDisabled = (row) => !!row?.isLockedByCci;
  const selectableCandidates = useMemo(
    () => (candidates || []).filter((c) => !isRowDisabled(c)),
    [candidates]
  );

  const totalCount = candidates?.length ?? 0;
  // LWI-870 — counts are derived from the *selectable* subset so the
  // "Select all" tri-state and the footer "N of M selected" line both
  // ignore locked rows the user can't act on.
  const selectableCount = selectableCandidates.length;
  const selectedCount = selectedPids.size;
  const allSelected = selectableCount > 0 && selectedCount === selectableCount;
  const someSelected = selectedCount > 0 && selectedCount < selectableCount;

  const handleToggleAll = () => {
    if (allSelected) {
      setSelectedPids(new Set());
    } else {
      setSelectedPids(new Set(selectableCandidates.map((c) => c.pid)));
    }
  };

  const handleToggleOne = (pid) => {
    setSelectedPids((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) {
        next.delete(pid);
      } else {
        next.add(pid);
      }
      return next;
    });
  };

  const handleConfirm = async () => {
    if (selectedCount === 0 || submitting) return;
    setSubmitting(true);
    setErrorMessage(null);
    try {
      await onConfirm(Array.from(selectedPids));
      // Caller closes us via onClose after successful onConfirm.
    } catch (err) {
      setErrorMessage(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to apply. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const rows = useMemo(() => candidates || [], [candidates]);

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="md"
      fullWidth
      data-testid="related-pos-bulk-action-dialog"
      PaperProps={{ sx: { borderRadius: 1 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontSize: fontSizes["2xl"],
          fontWeight: 600,
          pb: 2,
        }}
      >
        {title}
        <IconButton
          onClick={onClose}
          size="small"
          disabled={submitting}
          data-testid="related-pos-dialog-close"
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {description && (
          <Box sx={{ px: 3, py: 2, borderBottom: 1, borderColor: "divider" }}>
            <Typography sx={{ fontSize: fontSizes.base, color: "text.secondary" }}>
              {description}
            </Typography>
          </Box>
        )}

        {errorMessage && (
          <Box sx={{ px: 3, py: 2 }}>
            <Alert severity="error" data-testid="related-pos-dialog-error">
              {errorMessage}
            </Alert>
          </Box>
        )}

        <TableContainer component={Paper} elevation={0}>
          <Table size="small" data-testid="related-pos-table">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={handleToggleAll}
                    disabled={submitting || selectableCount === 0}
                    inputProps={{
                      "data-testid": "related-pos-select-all",
                    }}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>PO Number</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Line</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Part #</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Project</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Program</TableCell>
                <TableCell sx={{ fontWeight: 600 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const isSelected = selectedPids.has(row.pid);
                const rowDisabled = isRowDisabled(row);
                const rowClickable = !submitting && !rowDisabled;
                return (
                  <TableRow
                    key={row.pid}
                    hover={rowClickable}
                    onClick={() => rowClickable && handleToggleOne(row.pid)}
                    sx={{
                      cursor: rowClickable ? "pointer" : "default",
                      bgcolor: rowDisabled ? "action.hover" : "inherit",
                    }}
                    data-testid={`related-pos-row-${row.pid}`}
                  >
                    <TableCell padding="checkbox">
                      {rowDisabled ? (
                        <Tooltip title="A CCI referencing this PO line has already been submitted or approved — license edits are locked.">
                          <span>
                            <Checkbox checked={false} disabled />
                          </span>
                        </Tooltip>
                      ) : (
                        <Checkbox
                          checked={isSelected}
                          disabled={submitting}
                          onChange={() => handleToggleOne(row.pid)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                    </TableCell>
                    <TableCell>{row.poNumber}</TableCell>
                    <TableCell>{row.lineNumber ?? "—"}</TableCell>
                    <TableCell>{row.item ?? "—"}</TableCell>
                    <TableCell>{row.project ?? "—"}</TableCell>
                    <TableCell>{row.programName ?? "—"}</TableCell>
                    <TableCell>
                      {rowDisabled && (
                        <Chip
                          label="CCI submitted/approved — locked"
                          size="small"
                          color="warning"
                          variant="outlined"
                          data-testid={`related-pos-locked-chip-${row.pid}`}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions
        sx={{ p: 2, justifyContent: "space-between" }}
        data-testid="related-pos-dialog-actions"
      >
        <Typography sx={{ fontSize: fontSizes.md, color: "text.secondary", pl: 1 }}>
          {selectedCount} of {selectableCount} selected
          {totalCount > selectableCount &&
            ` (${totalCount - selectableCount} locked)`}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={onClose} disabled={submitting} sx={{ textTransform: "none" }}>
            {cancelLabel}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirm}
            disabled={selectedCount === 0 || submitting}
            sx={{ textTransform: "none", minWidth: 140 }}
            data-testid="related-pos-dialog-confirm"
          >
            {submitting ? <CircularProgress size={20} color="inherit" /> : confirmLabel}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
